const GA_MEASUREMENT_ID = (process.env.REACT_APP_GA_MEASUREMENT_ID || "").trim();
const YANDEX_METRIKA_ID = (process.env.REACT_APP_YANDEX_METRIKA_ID || "").trim();
const GTAG_SCRIPT_ID = "google-analytics-gtag";
const YANDEX_METRIKA_SCRIPT_ID = "yandex-metrika-tag";

export const COOKIE_CONSENT_STORAGE_KEY = "cookie_consent";
export const COOKIE_CONSENT_CHANGE_EVENT = "cookie-consent-changed";
export const COOKIE_CONSENT_ACCEPTED = "accepted";
export const COOKIE_CONSENT_REJECTED = "rejected";

let googleAnalyticsReady = false;
let googleAnalyticsLoading = null;
let yandexMetrikaReady = false;
let yandexMetrikaLoading = null;
let yandexLastHitPath = null;
let fallbackCookieConsentStatus = null;

function isGoogleAnalyticsConfigured() {
    return process.env.NODE_ENV === "production" && Boolean(GA_MEASUREMENT_ID);
}

function isYandexMetrikaConfigured() {
    return process.env.NODE_ENV === "production" && Boolean(YANDEX_METRIKA_ID);
}

export function isAnalyticsConfigured() {
    return isGoogleAnalyticsConfigured() || isYandexMetrikaConfigured();
}

export function getCookieConsentStatus() {
    if (typeof window === "undefined") {
        return null;
    }

    try {
        const raw = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
        if (!raw) {
            return null;
        }

        const consent = JSON.parse(raw);
        if (consent?.status === COOKIE_CONSENT_ACCEPTED || consent?.status === COOKIE_CONSENT_REJECTED) {
            fallbackCookieConsentStatus = consent.status;
            return consent.status;
        }
    } catch (error) {
        return fallbackCookieConsentStatus;
    }

    return fallbackCookieConsentStatus;
}

export function hasAnalyticsConsent() {
    return getCookieConsentStatus() === COOKIE_CONSENT_ACCEPTED;
}

export function setCookieConsentStatus(status) {
    if (typeof window === "undefined") {
        return;
    }

    fallbackCookieConsentStatus = status;
    try {
        window.localStorage.setItem(
            COOKIE_CONSENT_STORAGE_KEY,
            JSON.stringify({ status, at: Date.now() }),
        );
    } catch (error) {
        // Keep the in-memory choice for the current session when storage is blocked.
    }
    updateAnalyticsConsent(status);
    if (status === COOKIE_CONSENT_REJECTED) {
        cleanupAnalyticsCookies();
    }
    window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_CHANGE_EVENT, { detail: { status } }));
}

function ensureDataLayer() {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() {
        window.dataLayer.push(arguments);
    };
}

function getConsentSettings(status) {
    const analyticsStorage = status === COOKIE_CONSENT_ACCEPTED ? "granted" : "denied";

    return {
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
        analytics_storage: analyticsStorage,
    };
}

function bootstrapAnalyticsTag() {
    ensureDataLayer();
    window.gtag("consent", "default", getConsentSettings(getCookieConsentStatus()));
    window.gtag("js", new Date());
    window.gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });
    window.__fishmapAnalyticsInitialized = true;
}

export function updateAnalyticsConsent(status = getCookieConsentStatus()) {
    if (typeof window === "undefined" || !isGoogleAnalyticsConfigured()) {
        return;
    }

    ensureDataLayer();
    window.gtag("consent", "update", getConsentSettings(status));
}

function initializeGoogleAnalytics() {
    if (typeof window === "undefined" || !isGoogleAnalyticsConfigured()) {
        return Promise.resolve(false);
    }

    if (!window.__fishmapAnalyticsInitialized) {
        bootstrapAnalyticsTag();
    }
    updateAnalyticsConsent();

    if (googleAnalyticsReady) {
        return Promise.resolve(true);
    }

    if (googleAnalyticsLoading) {
        return googleAnalyticsLoading;
    }

    googleAnalyticsLoading = new Promise((resolve) => {
        if (document.getElementById(GTAG_SCRIPT_ID)) {
            googleAnalyticsReady = true;
            resolve(true);
            return;
        }

        const script = document.createElement("script");
        script.id = GTAG_SCRIPT_ID;
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
        script.onload = () => {
            googleAnalyticsReady = true;
            resolve(true);
        };
        script.onerror = () => {
            googleAnalyticsLoading = null;
            resolve(false);
        };
        document.head.appendChild(script);
    });

    return googleAnalyticsLoading;
}

function initializeYandexMetrika() {
    if (typeof window === "undefined" || !isYandexMetrikaConfigured() || !hasAnalyticsConsent()) {
        cleanupAnalyticsCookies();
        return Promise.resolve(false);
    }

    window.ym = window.ym || function ym() {
        (window.ym.a = window.ym.a || []).push(arguments);
    };
    window.ym.l = window.ym.l || Number(new Date());

    if (yandexMetrikaReady) {
        return Promise.resolve(true);
    }

    if (yandexMetrikaLoading) {
        return yandexMetrikaLoading;
    }

    yandexMetrikaLoading = new Promise((resolve) => {
        if (document.getElementById(YANDEX_METRIKA_SCRIPT_ID)) {
            yandexMetrikaReady = true;
            resolve(true);
            return;
        }

        const script = document.createElement("script");
        script.id = YANDEX_METRIKA_SCRIPT_ID;
        script.async = true;
        script.src = `https://mc.yandex.ru/metrika/tag.js?id=${encodeURIComponent(YANDEX_METRIKA_ID)}`;
        script.onload = () => {
            yandexMetrikaReady = true;
            yandexLastHitPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            window.ym(Number(YANDEX_METRIKA_ID), "init", {
                ssr: true,
                webvisor: true,
                clickmap: true,
                ecommerce: "dataLayer",
                referrer: document.referrer,
                url: window.location.href,
                accurateTrackBounce: true,
                trackLinks: true,
            });
            resolve(true);
        };
        script.onerror = () => {
            yandexMetrikaLoading = null;
            resolve(false);
        };
        document.head.appendChild(script);
    });

    return yandexMetrikaLoading;
}

function expireCookie(name, domain) {
    const domainPart = domain ? `; domain=${domain}` : "";
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0${domainPart}`;
}

function getCookieDomains() {
    const host = window.location.hostname;
    const baseDomain = host.split(".").slice(-2).join(".");

    return ["", host, `.${host}`, baseDomain, `.${baseDomain}`];
}

function cleanupCookieNames(cookieNames) {
    const domains = getCookieDomains();

    cookieNames.forEach((name) => {
        domains.forEach((domain) => expireCookie(name, domain));
    });
}

function cleanupGoogleAnalyticsCookies() {
    const cookieNames = document.cookie
        .split(";")
        .map((cookie) => cookie.trim().split("=")[0])
        .filter((name) => (
            name === "_ga"
            || name === "_gid"
            || name === "_gat"
            || name.startsWith("_ga_")
            || name.startsWith("_gac_")
            || name.startsWith("_gcl_")
        ));

    cleanupCookieNames(cookieNames);
}

function cleanupYandexMetrikaCookies() {
    cleanupCookieNames(["_ym_uid", "_ym_d", "_ym_isad", "_ym_visorc", "_ym_retryReqs", "yabs-sid"]);
}

function cleanupAnalyticsCookies() {
    if (typeof document === "undefined" || typeof window === "undefined") {
        return;
    }

    cleanupGoogleAnalyticsCookies();
    cleanupYandexMetrikaCookies();
}

export function initializeAnalytics() {
    if (typeof window === "undefined" || !isAnalyticsConfigured()) {
        return Promise.resolve(false);
    }

    if (!hasAnalyticsConsent()) {
        updateAnalyticsConsent(COOKIE_CONSENT_REJECTED);
        cleanupAnalyticsCookies();
        return Promise.resolve(false);
    }

    return Promise.all([initializeGoogleAnalytics(), initializeYandexMetrika()]).then(
        (results) => results.some(Boolean),
    );
}

export function trackPageView(path) {
    if (typeof window === "undefined" || !isAnalyticsConfigured() || !hasAnalyticsConsent()) {
        return;
    }

    initializeAnalytics().then(() => {
        if (isGoogleAnalyticsConfigured() && typeof window.gtag === "function") {
            window.gtag("event", "page_view", {
                page_path: path,
                page_location: window.location.href,
                page_title: document.title,
            });
        }

        if (isYandexMetrikaConfigured() && typeof window.ym === "function" && yandexLastHitPath !== path) {
            window.ym(Number(YANDEX_METRIKA_ID), "hit", window.location.href, {
                title: document.title,
                referer: document.referrer,
            });
            yandexLastHitPath = path;
        }
    });
}
